import React, { useMemo, useState } from "react";
import {
    AddIcon,
    ButtonWrapperDiv,
    CloneIcon,
    DeleteIcon,
    EditIcon,
    EditOffIcon,
    PathwayIconWrapperButton,
    RefreshIcon,
    SaveIcon,
    SettingsIcon,
    SpinnerIcon,
} from "../StyledComponents/PathwayIcons";
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Tooltip, UncontrolledButtonDropdown } from "reactstrap";
import PropTypes from "prop-types";
import stringUtils from "../../_services/string-utils";
import styled from "styled-components";

const iconButtonPropTypes = {
    disabled: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    dataCy: PropTypes.string,
    iconSize: PropTypes.string,
};

export const PathwayDeleteButton = ({ onClick, disabled, dataCy, iconSize, style }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy} style={style}>
            <DeleteIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayDeleteButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwayCloneButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconWrapperButton
            disabled={disabled}
            onClick={onClick}
            data-cy={dataCy}
            aria-label="Konfiguration klonen"
            title="Konfiguration klonen"
        >
            <CloneIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayCloneButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwayRefreshButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <RefreshIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayRefreshButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwayAddButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <AddIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayAddButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwayEditButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <EditIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayEditButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwayEditOffButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <EditOffIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayEditOffButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwaySettingsButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <SettingsIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwaySettingsButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwaySaveButton = ({ onClick, disabled, dataCy, color, iconSize }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <SaveIcon color={color} size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwaySaveButton.propTypes = {
    ...iconButtonPropTypes,
    color: PropTypes.string,
};

export const PathwayButton = ({
    children,
    onClick,
    color,
    disabled,
    dataCy,
    tooltip,
    tooltipWidth,
    tooltipPlacement = "right",
    fontColor,
    ariaPressed,
}) => {
    const [isTooltipOpen, toggleTooltip] = useState(false);
    const identifier = useMemo(() => {
        return "B" + stringUtils.randomString();
    }, []);

    return (
        <ButtonWrapperDiv id={identifier}>
            <Button
                color={color ? color : "primary"}
                data-cy={dataCy}
                disabled={disabled}
                onClick={onClick}
                aria-pressed={ariaPressed}
                style={{ color: fontColor }}
            >
                {children}
            </Button>

            {!stringUtils.isEmpty(tooltip) && (
                <Tooltip
                    style={{
                        backgroundColor: "rgb(240, 243, 245)",
                        color: "black",
                        maxWidth: tooltipWidth,
                    }}
                    placement={tooltipPlacement}
                    isOpen={isTooltipOpen}
                    target={identifier}
                    toggle={() => toggleTooltip(!isTooltipOpen)}
                >
                    {tooltip}
                </Tooltip>
            )}
        </ButtonWrapperDiv>
    );
};
PathwayButton.propTypes = {
    children: PropTypes.any.isRequired,
    onClick: PropTypes.func,
    color: PropTypes.string,
    disabled: PropTypes.bool,
    dataCy: PropTypes.string,
    tooltipPlacement: PropTypes.string,
    fontColor: PropTypes.string,
};

export const PathwayLinkButton = ({ children, onClick, disabled, dataCy, style }) => {
    return (
        <CustomButton color="link" data-cy={dataCy} disabled={disabled} onClick={(e) => onClick(e)}>
            {children}
        </CustomButton>
    );
};

const CustomButton = styled(Button)`
    vertical-align: baseline !important;
    padding: 0;
`;

PathwayLinkButton.propTypes = {
    children: PropTypes.any.isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    dataCy: PropTypes.string,
};

export const LoadingSpinnerButton = ({
    name,
    loading,
    children,
    onClick,
    disabled,
    color,
    buttonCyData,
    size,
    tooltip,
    tooltipWidth,
    tooltipPlacement = "right",
    style,
}) => {
    const [isTooltipOpen, toggleTooltip] = useState(false);
    const identifier = useMemo(() => {
        return "B" + stringUtils.randomString();
    }, []);

    return (
        <ButtonWrapperDiv style={style}>
            <Button
                name={name}
                id={identifier}
                color={color ? color : "primary"}
                data-cy={buttonCyData}
                disabled={loading || !!disabled}
                onClick={onClick}
            >
                {loading ? <SpinnerIcon data-cy={buttonCyData} $size={size} /> : children}
            </Button>

            {!stringUtils.isEmpty(tooltip) && (
                <Tooltip
                    style={{
                        backgroundColor: "rgb(240, 243, 245)",
                        color: "black",
                        maxWidth: tooltipWidth,
                    }}
                    isOpen={isTooltipOpen}
                    placement={tooltipPlacement}
                    target={identifier}
                    toggle={() => toggleTooltip(!isTooltipOpen)}
                >
                    {tooltip}
                </Tooltip>
            )}
        </ButtonWrapperDiv>
    );
};
LoadingSpinnerButton.propTypes = {
    loading: PropTypes.bool.isRequired,
    children: PropTypes.any.isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    color: PropTypes.string,
    buttonCyData: PropTypes.string,
    size: PropTypes.string,
    tooltip: PropTypes.string,
    tooltipWidth: PropTypes.string,
    tooltipPlacement: PropTypes.string,
};

export const LoadingSpinnerDropdownButton = ({
    loading,
    children,
    items,
    disabled,
    color,
    tooltip,
    tooltipWidth,
    tooltipPlacement,
    buttonCyData,
    size,
}) => {
    const [isTooltipOpen, toggleTooltip] = useState(false);
    const identifier = useMemo(() => {
        return "B" + stringUtils.randomString();
    }, []);

    return (
        <ButtonWrapperDiv id={identifier}>
            <UncontrolledButtonDropdown>
                <DropdownToggle
                    className="btn"
                    data-cy={buttonCyData}
                    color={color ? color : "primary"}
                    disabled={loading || disabled}
                    style={{ borderRadius: "0.25rem" }}
                >
                    {loading ? <SpinnerIcon data-cy={buttonCyData} $size={size} /> : children}
                </DropdownToggle>
                <DropdownMenu>
                    {items.map((entry, index) => {
                        if (entry === null) {
                            return <DropdownItem key={index} style={{ margin: "0" }} divider />;
                        } else {
                            return (
                                <DropdownItem
                                    style={{ border: "none" }}
                                    key={index}
                                    disabled={entry[2]}
                                    onClick={entry[1]}
                                    data-cy={entry[3] ? entry[3] : undefined}
                                >
                                    {entry[0]}
                                </DropdownItem>
                            );
                        }
                    })}
                </DropdownMenu>
            </UncontrolledButtonDropdown>

            {!stringUtils.isEmpty(tooltip) && (
                <Tooltip
                    style={{
                        backgroundColor: "rgb(240, 243, 245)",
                        color: "black",
                        maxWidth: tooltipWidth,
                    }}
                    placement={tooltipPlacement}
                    isOpen={isTooltipOpen}
                    target={identifier}
                    toggle={() => toggleTooltip(!isTooltipOpen)}
                >
                    {tooltip}
                </Tooltip>
            )}
        </ButtonWrapperDiv>
    );
};

LoadingSpinnerDropdownButton.propTypes = {
    loading: PropTypes.bool.isRequired,
    children: PropTypes.any.isRequired,
    items: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    color: PropTypes.string,
    buttonCyData: PropTypes.string,
    size: PropTypes.string,
    tooltip: PropTypes.string,
    tooltipWidth: PropTypes.string,
    tooltipPlacement: PropTypes.string,
};
